/* eslint-disable camelcase */
import { useContext, useEffect, useState } from 'react';

import { createClientEvent } from '@buzzfeed/client-event-tracking';
import {
  content_action,
  impression,
  external_link,
  internal_link,
  pageview,
  quiz_answer,
  quiz_complete,
  time_spent,
} from '@buzzfeed/client-event-tracking/events';
import { createClientTimeSpentHandler } from '@buzzfeed/client-event-tracking/handlers';
import { TrackingContext } from '../context/TrackingContext';
import { CLUSTER } from '../constants';

const sendClientEvent = createClientEvent({
  env: CLUSTER,
  source: 'web_bf',
});

const createClientEventWithCommonData =
  (event_config) =>
  (...layers) => {
    sendClientEvent(event_config, ...layers);
  };

/*********** tasty tracking ************/

const sendTastyClientEvent = createClientEvent({
  amp_options: {
    // in order to include amp_id for connecting AMP and non-AMP events
    track_amp: true,
  },
  env: CLUSTER,
  source: 'web_tasty',
});

const createTastyClientEventWithCommonData =
  (event_config) =>
  (...layers) => {
    sendTastyClientEvent(event_config, ...layers);
  };

// complex tracking
const sendComplexClientEvent = createClientEvent({
  env: CLUSTER,
  source: 'web_complex',
});

const createComplexClientEventWithCommonData =
  (event_config) =>
  (...layers) => {
    sendComplexClientEvent(event_config, ...layers);
  };

// huff post tracking
const sendHuffPostClientEvent = createClientEvent({
  env: CLUSTER,
  source: 'web_huffpost',
});

const createHuffPostClientEventWithCommonData =
  (event_config) =>
  (...layers) => {
    sendHuffPostClientEvent(event_config, ...layers);
  };

// conjure tracking
const sendConjureClientEvent = createClientEvent({
  env: CLUSTER,
  source: 'conjure_web',
});

const createConjureClientEventWithCommonData =
  (event_config) =>
  (...layers) => {
    sendConjureClientEvent(event_config, ...layers);
  };

const trackClientImpression = createClientEventWithCommonData(impression);
const trackClientExternalLink = createClientEventWithCommonData(external_link);
const trackClientInternalLink = createClientEventWithCommonData(internal_link);
const trackClientContentAction =
  createClientEventWithCommonData(content_action);

const trackClientPageview = createClientEventWithCommonData(pageview);
const trackTimeSpent = createClientEventWithCommonData(time_spent);

const trackQuizAnswer = createClientEventWithCommonData(quiz_answer);
const trackQuizComplete = createClientEventWithCommonData(quiz_complete);

export const attachTimeSpentHandler = createClientTimeSpentHandler(
  ({ layers }, ...additionalData) => {
    trackTimeSpent(...additionalData, ...layers);
  }
);

const trackConjureContentAction = createConjureClientEventWithCommonData(content_action);
const trackConjureExternalLink = createConjureClientEventWithCommonData(external_link);
const trackConjurePageview = createConjureClientEventWithCommonData(pageview);
const trackConjureTimeSpent = createConjureClientEventWithCommonData(time_spent);
const trackConjureQuizComplete = createConjureClientEventWithCommonData(quiz_complete);
const trackConjureImpression = createConjureClientEventWithCommonData(impression);

export const attachConjureTimeSpentHandler = createClientTimeSpentHandler(
  ({ layers }, ...additionalData) => {
    trackConjureTimeSpent(...additionalData, ...layers);
  }
);

const trackTastyClientContentAction =
  createTastyClientEventWithCommonData(content_action);

const trackComplexClientContentAction =
  createComplexClientEventWithCommonData(content_action);

const trackHuffPostClientContentAction =
  createHuffPostClientEventWithCommonData(content_action);

/*-----------------------------------------*/
const getTrackingData = () => {
  if (window && window.frameElement) {
    const embedWrapper = window.frameElement.parentNode.closest(
      '[data-name="bfp-wrapper"]'
    );
    if (embedWrapper) {
      const trackingDataScript = embedWrapper.querySelectorAll(
        'script[data-name="tracking-data"]'
      );
      if (trackingDataScript?.[0]) {
        const trackingData = JSON.parse(trackingDataScript?.[0].text);
        return trackingData;
      }
    }
  }

  return false;
};

export const useTrackingContext = ({ isTasty = false, isConjure = false } = {}) => {
  const [parentTrackingData, setParentTrackingData] = useState({});
  const pageInfo = useContext(TrackingContext);

  useEffect(() => {
    const trackingData = getTrackingData();

    if (trackingData) {
      setParentTrackingData(trackingData);
    }
  }, []);

  const withPageInfo = (trackingAction) => (eventInfo) => {
    const newTrackingInfo = {
      ...pageInfo,
      ...eventInfo,
      ...parentTrackingData,
    };

    // TEMPORARY FIX!
    // if the eventInfo has item_type or item_name, we should override the parentTrackingData's item_type and item_name
    // PROPER FIX needs to be
    // in combination with avoiding any hard coded unit_name/unit_type/buzz_id overrides coming from individual tracking events in each quiz/chat/widget.
    // const withPageInfo = (trackingAction) => (eventInfo) =>
    //   trackingAction({ ...parentTrackingData, ...pageInfo, ...eventInfo });
    if (eventInfo.item_type) {
      newTrackingInfo.item_type = eventInfo.item_type;
    }
    if (eventInfo.item_name) {
      newTrackingInfo.item_name = eventInfo.item_name;
    }

    return trackingAction(newTrackingInfo);
  };

  return {
    trackImpression: isConjure
      ? withPageInfo(trackConjureImpression)
      : withPageInfo(trackClientImpression),
    trackExternalLink: isConjure
      ? withPageInfo(trackConjureExternalLink)
      : withPageInfo(trackClientExternalLink),
    trackInternalLink: withPageInfo(trackClientInternalLink),
    trackContentAction: isConjure
      ? withPageInfo(trackConjureContentAction)
      : isTasty
        ? withPageInfo(trackTastyClientContentAction)
        : withPageInfo(trackClientContentAction),
    trackPageview: isConjure
      ? withPageInfo(trackConjurePageview)
      : withPageInfo(trackClientPageview),
    trackQuizAnswer: withPageInfo(trackQuizAnswer),
    trackQuizComplete: isConjure
      ? withPageInfo(trackConjureQuizComplete)
      : withPageInfo(trackQuizComplete),
    trackTimeSpent: isConjure
      ? withPageInfo(trackConjureTimeSpent)
      : withPageInfo(trackTimeSpent),
    attachTimeSpentHandler: isConjure
      ? attachConjureTimeSpentHandler
      : attachTimeSpentHandler,

    // complex
    trackComplexContentAction: withPageInfo(trackComplexClientContentAction),

    // huff post
    trackHuffPostContentAction: withPageInfo(trackHuffPostClientContentAction),
  };
};
